import { Menu, X } from "lucide-react";
import LogoLight from "../../assets/logo.webp";
import LogoDark from "../../assets/logo_dark.webp";
import { Link as ScrollLink } from "react-scroll";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Button from "../ui/Button";

const LINKS = [
  { name: "Digital World", path: "digital-world" },
  { name: "Services", path: "services" },
  { name: "Projects", path: "projects" },
  { name: "Products", path: "product" },
  { name: "Trust", path: "trust" },
  { name: "Approach", path: "approach" },
  { name: "NOTCH", path: "notifications" },
  { name: "Team", path: "team" },
];

// Both product sections (Lynqu, Flat Rent Budapest) share the one
// "Products" nav item, so the second id resolves to its path.
const SECTION_ALIAS = { flatrent: "product" };

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

const Nav = (props) => {
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(LINKS[0].path);
  const panelRef = useRef(null);
  const toggleRef = useRef(null);

  /* Which section is active is "which one is crossing the middle of the
   * viewport", not "which one is 70% visible". The old threshold of 0.7 can
   * never be met by a section taller than the viewport — which is most of them
   * on a phone — so those nav items never lit up at all. */
  useEffect(() => {
    const sections = document.querySelectorAll("section");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(SECTION_ALIAS[entry.target.id] || entry.target.id);
          }
        });
      },
      { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const closeMenu = useCallback(() => setOpen(false), []);

  /* Escape closes, and focus goes back to the control that opened it. */
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        if (toggleRef.current) toggleRef.current.focus();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      /* Focus stays inside the open drawer. Without this, Tab walked straight
       * out of it and on through the page behind. */
      const items = [
        toggleRef.current,
        ...panelRef.current.querySelectorAll(FOCUSABLE),
      ].filter(Boolean);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open]);

  /* The page must not scroll behind an open drawer. */
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  /* A closed drawer is not just off screen — it is out of the tab order and
   * out of the accessibility tree. Previously eight links sat at right:-100%,
   * still focusable, so tabbing from the logo walked off into nothing.
   * Above nav-sm the same <ul> IS the desktop nav, so it must stay live. */
  useEffect(() => {
    const sync = () => {
      const narrow = window.innerWidth < 860;
      if (!narrow && open) setOpen(false);
      if (panelRef.current) panelRef.current.inert = narrow && !open;
    };
    sync();
    window.addEventListener("resize", sync);
    return () => window.removeEventListener("resize", sync);
  }, [open]);

  // Warm both logo variants once — they cross-fade with the section theme.
  useEffect(() => {
    [LogoLight, LogoDark].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Frosted-glass nav matched to lynqu.com's recipe: translucent fill +
  // blur + saturate + hairline border + soft elevation shadow, tuned per
  // theme so it reads over light, gray, and black sections alike. The hero
  // ("transparent") returns no fill — the capsule is invisible there.
  //
  // The fill carries the CONTRAST, and the sections it floats over are
  // arbitrary photography and video, so it cannot be thinned to taste. It was
  // worse than thin: /72 is not a step in Tailwind's opacity scale, so it
  // compiled to NOTHING and the capsule shipped with no fill at all — blur,
  // border and shadow over raw video. Blur does not rescue that; blurring a
  // dark photo gives you a dark surface. Every value here must be a real step
  // (0-100 by 5) or an arbitrary /[0.xx]; scripts/check-type-scale.mjs fails
  // the build on the ones that are neither.
  const GLASS_DARK =
    "bg-[#1C1B20]/85 backdrop-blur-[20px] backdrop-saturate-[180%] border border-white/[0.08] shadow-[0_8px_28px_0_rgba(0,0,0,0.3)]";
  const cache = useRef(GLASS_DARK);

  const backgroundColor = (input) => {
    switch (input) {
      case "transparent":
        return "";
      case "light":
        cache.current =
          "bg-white/90 backdrop-blur-[20px] backdrop-saturate-[180%] border border-[#E0DDE6]/50 shadow-[0_8px_28px_0_rgba(0,0,0,0.08)]";
        return cache.current;
      case "dark":
        cache.current = GLASS_DARK;
        return cache.current;
      case "gray":
        cache.current =
          "bg-[#292929]/85 backdrop-blur-[20px] backdrop-saturate-[180%] border border-white/[0.08] shadow-[0_8px_28px_0_rgba(0,0,0,0.3)]";
        return cache.current;
      default:
        return cache.current;
    }
  };

  // The open mobile drawer must stay fully readable over ANY section, so the
  // panel is near-opaque and theme-matched.
  const menuBackground = (input) => {
    switch (input) {
      case "light":
        return "bg-white/95 backdrop-blur-[16px] border border-[#E0DDE6]/60 shadow-[0_12px_40px_rgba(0,0,0,0.14)]";
      case "gray":
        return "bg-[#292929]/95 backdrop-blur-[16px] border border-white/[0.08] shadow-[0_12px_40px_rgba(0,0,0,0.4)]";
      default:
        return "bg-[#1C1B20]/95 backdrop-blur-[16px] border border-white/[0.08] shadow-[0_12px_40px_rgba(0,0,0,0.4)]";
    }
  };

  const navItemColor = (input) => (input === "light" ? "text-navLink" : "text-[#ffffff]");
  const buttonColor = (input) => {
    switch (input) {
      case "light":
        return "button-shadow";
      case "dark":
        return "button-shadow-dark";
      default:
        return "button-shadow-normal";
    }
  };
  const iconTint = (input) => (input === "light" ? "#000" : "#fff");
  const isLight = props.theme === "light";

  return (
    <nav className="navbar">
      <div
        className={`nav-capsule h-full nav-sm:flex items-center justify-between nav-sm:rounded-full nav-sm:overflow-hidden nav-sm:px-[8px] ${backgroundColor(
          props.theme
        )}`}>
        <div className="w-[107px] h-full flex items-center justify-center ml-[28px] nav-sm:ml-[24px] c-lg:ml-[28px] c-xl:ml-[36px]">
          <button
            type="button"
            aria-label="Scroll to top"
            className="logo h-[50px] w-[107px] cursor-pointer relative flex items-center justify-center"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            {/* Both marks are painted and cross-faded. Swapping one <img>'s src
                mid-scroll was a hard cut between two different images. */}
            <img
              src={LogoDark}
              alt="Gravisun"
              width={317}
              height={108}
              className={`w-full h-auto transition-opacity duration-300 ${isLight ? "opacity-0" : "opacity-100"}`}
            />
            <img
              src={LogoLight}
              alt=""
              aria-hidden="true"
              width={317}
              height={108}
              className={`absolute inset-0 m-auto w-full h-auto transition-opacity duration-300 ${isLight ? "opacity-100" : "opacity-0"}`}
            />
          </button>
        </div>

        <button
          ref={toggleRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="nav-ul"
          className="text-3xl absolute right-[22px] top-[8px] m12p:top-[14px] grid place-items-center w-[44px] h-[44px] cursor-pointer nav-sm:hidden">
          {open ? <X color={iconTint(props.theme)} /> : <Menu color={iconTint(props.theme)} />}
        </button>

        <ul
          id="nav-ul"
          ref={panelRef}
          aria-hidden={!open ? "true" : undefined}
          className={`nav-menu-panel nav-sm:flex ${open ? menuBackground(props.theme) : ""}
                     nav-sm:!bg-transparent nav-sm:!border-0 nav-sm:!shadow-none
                     nav-sm:items-center nav-sm:pb-0 pb-[28px] pt-[16px] nav-sm:pt-0 absolute nav-sm:static nav-sm:z-auto
                     right-0 rounded-l-[24px] nav-sm:rounded-none
                     w-[287px] nav-sm:w-auto px-[48px] nav-sm:px-0
                     nav-sm:!visible nav-sm:!opacity-100 nav-sm:!translate-x-0
                     ${open ? "visible opacity-100 translate-x-0" : "invisible opacity-0 translate-x-full"}`}
          /* transform + opacity, and a decelerating curve because the panel is
             ARRIVING. It used to animate `right` from -100% with ease-in —
             a layout property, on the wrong curve. */
          style={{
            transitionProperty: "transform, opacity, visibility",
            transitionDuration: "320ms",
            transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.21, 1)",
            willChange: open ? "transform, opacity" : "auto",
          }}>
          {LINKS.map((item, index) => (
            <NavItem
              key={item.path}
              onClick={() => {
                closeMenu();
                setActiveSection(item.path);
              }}
              textColor={navItemColor(props.theme)}
              name={item.name}
              path={item.path}
              isSelected={
                activeSection === item.path ||
                (activeSection === "" && index === LINKS.length - 1)
              }
            />
          ))}
          {/* <li> (not <div>): a <ul> may only contain li/script/template
              children — anything else breaks screen-reader list semantics. */}
          <li className="list-none flex w-full nav-sm:w-auto items-center justify-center ml-0 nav-sm:ml-[32px] lg:ml-[50px] nav-sm:mr-[3px] mt-[36px] m14p:mt-[45px] nav-sm:mt-0">
            <Button
              onClick={props.onContactUsClicked}
              title="BOOK NOW"
              color={buttonColor(props.theme)}
              textColor={navItemColor(props.theme)}
            />
          </li>
        </ul>
      </div>
    </nav>
  );
};

const NavItem = (props) => {
  const base =
    "nav-sm:ml-[12px] lg:ml-[24px] nav-sm:my-0 my-[14px] m14p:my-[18px] nav-underline block";
  const cls = props.isSelected
    ? `${base} is-active`
    : `${base} ${props.textColor || ""}`;

  // href makes the anchor crawlable; react-scroll's handleClick calls
  // preventDefault(), so the browser never performs the native fragment jump.
  return (
    <li className={cls}>
      <ScrollLink
        href={`#${props.path}`}
        onClick={() => props.onClick()}
        to={props.path}
        duration={300}
        className="inline-block py-[14px] -my-[14px] text-13 c-lg:text-14 c-xl:text-16 cursor-pointer duration-300 font-sans">
        {props.name}
      </ScrollLink>
    </li>
  );
};

export default Nav;
